import {
  booleanAttribute,
  Component,
  computed,
  forwardRef,
  inject,
  Injector,
  input,
  Input,
  model,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-floating-textarea',
  imports: [CommonModule, FormsModule],
  templateUrl: './floating-textarea.component.html',
  styleUrls: ['./floating-textarea.component.scss'],
  viewProviders: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FloatingTextareaComponent),
    },
  ],
})
export class FloatingTextareaComponent implements ControlValueAccessor, OnInit, OnDestroy {
  injector = inject(Injector);

  value = model('');

  // if it's null, the input is not required, but "Optional" isn't shown in label, this is for non-form usage
  // if it's false, the input is not required and "Optional" is shown in label
  // if it's true, the input is required
  required = input<boolean | null, string | boolean>(null, {
    transform: booleanAttribute,
  });

  name = input('textAreaName');
  label = input<string>();
  placeholder = input<string>();
  disabled = model(false);
  verticalSpacing = input<boolean, string | boolean>(true, {
    transform: booleanAttribute,
  });
  /**
   * validate input against a given pattern
   */
  @Input() pattern = null;
  @Input() patternErrorMessage: string = null;
  @Input() autoResize: boolean | undefined = false;
  @Input() rows = 5;
  @Input() cols = 30;

  isDestroyed$ = new Subject();

  readonly = true; // workaround against autocomplete

  protected ngControl = signal<NgControl | null>(null);
  private onChange = (value: string) => {};
  private onTouched = () => {};

  control = computed(() => {
    return this.ngControl()?.control;
  });

  ngOnInit() {
    this.ngControl.set(this.injector.get(NgControl, null));
    if (this.ngControl()) {
      this.ngControl().valueAccessor = this;

      if (!this.ngControl()?.control) {
        return;
      }

      if (this.required()) {
        this.ngControl().control.addValidators(Validators.required);
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  onBlur(): void {
    this.onTouched();
  }

  onModelChange(event: Event) {
    const value = (event.target as HTMLTextAreaElement).value;
    this.onChange(value);
    this.value.set(value);
    // this.formattedModel = value;
    // this.modelChange.emit(value);
  }

  onFocus() {
    // workaround against autocomplete
    // https://stackoverflow.com/a/32578659/6146963
    this.readonly = false;
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
